import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Grid,
  IconButton,
  Avatar,
} from "@material-ui/core";
import { useState, useEffect } from "react";
import { MerchantOrganisationDetailsResponseModel } from "../redux/actions/merchantDetails";
import CloseIcon from "@material-ui/icons/Close";

export interface EditMerchantData {
  Name?: string;
  PhoneNumber?: string;
  InfoEmail?: string;
  LogoBase64?: string;
  BrandColour?: string;
  Address: {
    Line1?: string;
    Line2?: string;
    Line3?: string;
    PostalCode?: string;
    County?: string;
    City?: string;
    Lat?: number;
    Long?: number;
  };
  VatNumber?: string;
  InvoiceName?: string;
  StripeAccountId?: string;
}

interface EditMerchantModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (data: EditMerchantData) => void;
  merchant?: MerchantOrganisationDetailsResponseModel;
  loading: boolean;
}

export const EditMerchantModal = ({
  visible,
  onClose,
  onSubmit,
  merchant,
  loading,
}: EditMerchantModalProps) => {
  const [formData, setFormData] = useState<EditMerchantData>({
    Name: '',
    PhoneNumber: '',
    InfoEmail: '',
    LogoBase64: '',
    BrandColour: '',
    Address: {
      Line1: '',
      Line2: '',
      Line3: '',
      PostalCode: '',
      County: '',
      City: '',
    },
    VatNumber: '',
    InvoiceName: '',
    StripeAccountId: '',
  });

  const [logoPreview, setLogoPreview] = useState<string>('');
  const [logoChanged, setLogoChanged] = useState<boolean>(false);

  useEffect(() => {
    if (merchant) {
      setFormData({
        Name: merchant.Name || '',
        PhoneNumber: merchant.PhoneNumber || '',
        InfoEmail: merchant.InfoEmail || '',
        LogoBase64: '', // Don't send existing logo URL as base64
        BrandColour: merchant.BrandColour || '',
        Address: {
          Line1: merchant.Address.Line1 || '',
          Line2: merchant.Address.Line2 || '',
          Line3: merchant.Address.Line3 || '',
          PostalCode: merchant.Address.PostalCode || '',
          County: merchant.Address.County || '',
          City: merchant.Address.City || '',
          Lat: merchant.Address.Lat,
          Long: merchant.Address.Long,
        },
        VatNumber: merchant.VatNumber || '',
        InvoiceName: merchant.InvoiceName || '',
        StripeAccountId: merchant.StripeAccountId || '',
      });
      setLogoPreview(merchant.LogoUrl || '');
      setLogoChanged(false); // Reset logo changed state
    }
  }, [merchant]);

  const handleInputChange = (field: string, value: string) => {
    if (field.startsWith('Address.')) {
      const addressField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        Address: {
          ...prev.Address,
          [addressField]: value,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Create a preview URL
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
      
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target?.result as string;
        setFormData(prev => ({
          ...prev,
          LogoBase64: base64String,
        }));
        setLogoChanged(true); // Mark that logo has been changed
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = () => {
    // Only include LogoBase64 if the logo has been changed
    const submitData = {
      ...formData,
      LogoBase64: logoChanged ? formData.LogoBase64 : undefined,
    };
    onSubmit(submitData);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={visible}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 12,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Edit Merchant</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box style={{ padding: '20px 0' }}>
          <Grid container spacing={3}>
            {/* Logo Upload Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Logo
              </Typography>
              <Box display="flex" alignItems="center" style={{ gap: 16 }}>
                <Avatar
                  src={logoPreview}
                  style={{ width: 80, height: 80 }}
                >
                  {merchant?.Name?.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="logo-upload"
                    type="file"
                    onChange={handleLogoUpload}
                  />
                  <label htmlFor="logo-upload">
                    <Button variant="outlined" component="span">
                      Upload Logo
                    </Button>
                  </label>
                  <Typography variant="caption" display="block" color="textSecondary">
                    Recommended size: 512x512px
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Basic Information */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.Name}
                onChange={(e) => handleInputChange('Name', e.target.value)}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.PhoneNumber}
                onChange={(e) => handleInputChange('PhoneNumber', e.target.value)}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Info Email"
                value={formData.InfoEmail}
                onChange={(e) => handleInputChange('InfoEmail', e.target.value)}
                variant="outlined"
                type="email"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="VAT Number"
                value={formData.VatNumber}
                onChange={(e) => handleInputChange('VatNumber', e.target.value)}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Invoice Name"
                value={formData.InvoiceName}
                onChange={(e) => handleInputChange('InvoiceName', e.target.value)}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Stripe Account ID"
                value={formData.StripeAccountId}
                onChange={(e) => handleInputChange('StripeAccountId', e.target.value)}
                variant="outlined"
                placeholder="acct_..."
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Brand Colour"
                value={formData.BrandColour}
                onChange={(e) => handleInputChange('BrandColour', e.target.value)}
                variant="outlined"
                placeholder="#000000"
              />
            </Grid>

            {/* Address Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom style={{ marginTop: 16 }}>
                Address
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 1"
                value={formData.Address.Line1}
                onChange={(e) => handleInputChange('Address.Line1', e.target.value)}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2"
                value={formData.Address.Line2}
                onChange={(e) => handleInputChange('Address.Line2', e.target.value)}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 3"
                value={formData.Address.Line3}
                onChange={(e) => handleInputChange('Address.Line3', e.target.value)}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Postal Code"
                value={formData.Address.PostalCode}
                onChange={(e) => handleInputChange('Address.PostalCode', e.target.value)}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="City"
                value={formData.Address.City}
                onChange={(e) => handleInputChange('Address.City', e.target.value)}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="County"
                value={formData.Address.County}
                onChange={(e) => handleInputChange('Address.County', e.target.value)}
                variant="outlined"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      
      <DialogActions style={{ padding: '16px 24px' }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
